import { CButton, CInput, CInputGroup, CInputGroupAppend, CLabel } from '@coreui/react';
import moment from 'moment';
import * as React from 'react';
import { IValue, KComplements, KControlAddon, KNumKeypad } from '../';
import { IImageProvider, IMessageBox, IVirtualKeyboard } from '../../abstraction';
import { Utils } from '../../core/utils';
import { ControlZoneEnum } from '../../models/ControlZoneEnum';
import { ControlModel } from '../../models/index';
import { InternalTypeEnum } from '../../models/InternalTypeEnum';

interface IProps {
	Control: ControlModel
	onChange?: (value: string) => void;
	onShortCutClick?: (actionName: string) => void;
	virtualKeyboard: IVirtualKeyboard;
	msgBoxProvider: IMessageBox;
	imageProvider: IImageProvider;
}

interface IState {
	currentControl: ControlModel
	showKeypad: boolean;
	virtualKeyboard: boolean;
}

export class KControlInput extends React.Component<IProps, IState> {

	inputRef: any;
	handleAutofocus: boolean = true;
	private separator: ":" | "." | "/" | " " = ".";

	public constructor(props: any) {
		super(props);

		this.state = {
			currentControl: this.props.Control,
			showKeypad: false,
			virtualKeyboard: this.props.virtualKeyboard.isVirtualKeyboardActive,
		}

		this.inputRef = React.createRef();

		// Permet de mettre à jour le mode du clavier virtuel en cas de changement
		this.props.virtualKeyboard.onVirtualKeyboardChanged.add((state) => { this.setState({ virtualKeyboard: state }) });
	}

	//#region Events

	// Désabonnement pour éviter les warning react
	componentWillUnmount = () => {
		this.props.virtualKeyboard.onVirtualKeyboardChanged.reset();
	}

	componentDidUpdate = () => {
		let ctrl = this.state.currentControl;

		if ((ctrl.HasFocus) && (ctrl.Value == "") && (this.handleAutofocus)) {
			if (this.inputRef.current) {
				this.inputRef.current.focus();
				this.handleAutofocus = false;
			}
		}

		if (ctrl.OnClick != "") {
			if (this.inputRef.current)
				this.inputRef.current.onclick = ctrl.OnClick;
		}
	}

	onFocus = () => {
		this.setState({ showKeypad: true });
	}

	onBlur = () => {
		if (this.state.showKeypad) {
			this.setState({
				showKeypad: false,
			});
		}
	}

	// Clic sur une touche de le pavé numérique
	onNumKeypadClick = (value: string) => {
		this.state.currentControl.Value = value;
		this.setState({ currentControl: this.state.currentControl });
	}

	// Clic sur le control
	// Le contrôle est découpé en 3 zones (LEFT/MIDDLE/RIGHT)
	// Les zones left & right peuvent contenir des addon (bouton images)
	// TODO: A mutualiser - Utilisé dans pas mal de controles
	onControlClick = (zone: ControlZoneEnum) => {
		let ctrl = this.state.currentControl;

		// On regarde pour chacune des zone si une Action est définie
		// Si oui, on simule un clic sur l'un des boutons actions de l'écran

		if (zone == ControlZoneEnum.left && ctrl.RunActionLeft) {
			if (this.props.onShortCutClick) this.props.onShortCutClick(ctrl.RunActionLeft);
		}
		else if (zone == ControlZoneEnum.middle && ctrl.RunActionMiddle) {
			if (this.props.onShortCutClick) this.props.onShortCutClick(ctrl.RunActionMiddle);
		}
		else if (zone == ControlZoneEnum.right && ctrl.RunActionRight) {
			if (this.props.onShortCutClick) this.props.onShortCutClick(ctrl.RunActionRight);
		}
		else {
			// Si rien de tout ça, alors on évalue le script lié au clic passé dans les attributs  (s'il existe)
			// ça permet de prévoir un script côté x3 mais c'est un peu déconseillé quand meme
			try {
				eval(ctrl.OnClick)
			}
			catch (err) {
				console.log(err);
				this.props.msgBoxProvider.MessageBox("Onclick Error in javascript", "Check console for more details", 3);
			}
		}
	}

	// Changement de la valeur principale
	onChange = (event) => {
		if (this.props.Control.Disable) return;
		let value: string = event.target.value;

		switch (this.props.Control.DataType) {
			case InternalTypeEnum.Alpha:
			case InternalTypeEnum.Blob:
			case InternalTypeEnum.Clob:
				if ((this.props.Control.MaxLength) && (this.props.Control.MaxLength > 0)) {
					if (value.length > this.props.Control.MaxLength) {
						return;
					}
				}
				break;

			case InternalTypeEnum.Uuid:
			case InternalTypeEnum.Menu:
				return "";

			case InternalTypeEnum.Date:
			case InternalTypeEnum.Datetime:
				break;

			case InternalTypeEnum.Decimal:
			case InternalTypeEnum.Double:
			case InternalTypeEnum.Float:
			case InternalTypeEnum.Short:
			case InternalTypeEnum.Long:

				if (this.props.Control.DataType == InternalTypeEnum.Decimal && this.props.Control.InputType == "time") {
					if (!moment(value, "HH:mm").isValid()) return;
				}
				else {
					if (isNaN(Number(value))) return;
				}
				break;
			default:
				break;
		}

		this.props.Control.Value = value;
		this.setState({ currentControl: this.state.currentControl })

		if (this.props.onChange) this.props.onChange(value);

		//try {
		//	eval(this.state.currentControl.OnChange)
		//}
		//catch (err) {
		//	console.log(err);
		//	dispatcher.MessageBox("Onchange Error in javascript", "Check console for more details", 3);
		//}
	}

	//#endregion

	//#region Rendus


	// Rendu de le pavé numérique
	renderNumKeypad = () => {
		// Contrôle désactivé => rien à rendre
		if (this.props.Control.Disable) return "";

		if (this.props.Control.InputType) {
			// Si on a spécifié le type via l'attribut inputtype = date, alors on ne s'occupe pas du pavé numérique
			if (" datetime date time number week".indexOf(" " + this.props.Control.InputType.toLowerCase()) != -1) {
				return "";
			}
		}

		switch (this.props.Control.DataType) {
			case InternalTypeEnum.Alpha:
			case InternalTypeEnum.Blob:
			case InternalTypeEnum.Clob:
			case InternalTypeEnum.Uuid:
			case InternalTypeEnum.Menu:
				return "";

			case InternalTypeEnum.Date:
			case InternalTypeEnum.Datetime:
			case InternalTypeEnum.Decimal:
			case InternalTypeEnum.Double:
			case InternalTypeEnum.Float:
			case InternalTypeEnum.Short:
			case InternalTypeEnum.Long:
			default:
				break;
		}

		return (
			<CInputGroupAppend>
				<KNumKeypad separator={this.separator} ctrl={this.state.currentControl as IValue} onMouseLeave={this.onBlur} onChange={this.onNumKeypadClick} visible={this.state.showKeypad}></KNumKeypad>
				<CButton onClick={() => { this.setState({ showKeypad: true }); }}>⌨</CButton>
			</CInputGroupAppend>
		);
	}

	render() {
		let ctrl = this.state.currentControl;
		if (!ctrl.Visible) return null;

		let inlineStyle = Utils.decodeInlineStyleFromJson(ctrl.InlineStyle);
		let gangnamStyle = Utils.decodeInlineStyleFromJson(ctrl.InputInlineStyle);

		let validity = ctrl.ShowValidity ? <span className="validity"></span> : "";
		let numKeypad = this.renderNumKeypad();
		let bsSize = Utils.getColSizeClasses(ctrl);
		let header = <CLabel htmlFor={this.props.Control.Key} >{ctrl.Description}</CLabel>

		// 
		return (
			<div className={`KControl KControlInput ${ctrl.Class} ${bsSize}`} style={inlineStyle}  >
				{header}

				<CInputGroup >
					<KControlAddon imageProvider={this.props.imageProvider} type="prepend" image={ctrl.PrependImage} text={ctrl.PrependText} onClick={() => this.onControlClick(ControlZoneEnum.left)} visibilityCondition={ctrl.PreVisibilityCond}/>

					<CInput
						name={this.props.Control.Key}
						value={ctrl.Value}
						style={gangnamStyle}
						autoFocus={ctrl.HasFocus}
						innerRef={this.inputRef}
						min={ctrl.Min}
						max={ctrl.Max}
						minLength={ctrl.MinLength}
						maxLength={ctrl.MaxLength}
						placeholder={ctrl.PlaceHolder}
						readOnly={ctrl.Disable}
						required={ctrl.Required}
						step={ctrl.Step}
						type={ctrl.InputType}
						onChange={this.onChange}
						onClick={() => this.onControlClick(ControlZoneEnum.middle)}
						inputMode={!this.state.virtualKeyboard ? "none" : "text"}
						pattern={ctrl.Pattern}
					>
					</CInput>
										
					{numKeypad}

					<KControlAddon imageProvider={this.props.imageProvider} type="append" image={ctrl.AppendImage} text={ctrl.AppendText} onClick={() => this.onControlClick(ControlZoneEnum.right)} visibilityCondition={ctrl.AppVisibilityCond}/>

					{validity}
				</CInputGroup>
				<CLabel className="KControlError " >{ctrl.ErrorMessage}</CLabel>
				<KComplements Control={ctrl} />
			</div>
		)
	}

	//#endregion 
} 